import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useUploadDocument } from "hooks/useUploadDocument";
import { ClientDocument } from "@compass/shared/types";

// Keep in sync with MAX_FILE_SIZE in components/form/UploadDocument.tsx (25MB).
const MAX_FILE_SIZE = 25 * 1024 * 1024;

export type UploadContext = "PUBLIC_ATTACHMENT" | "PRIVATE_ATTACHMENT";
export type NotifyContext = "discussion" | "conversation" | "social" | "documents";

/**
 * PRTL-1125: upload a batch of files as attachments, looping `useUploadDocument`
 * one file at a time. Oversized files (> 25MB) and per-file failures surface a
 * snackbar and are skipped — a single bad file never aborts the rest of the
 * batch. Returns the documents that uploaded successfully so callers can merge
 * them into their attachment state (de-duping by id).
 */
export const useUploadFiles = (context: UploadContext, notifyContext?: NotifyContext) => {
    const uploadDocument = useUploadDocument();
    const { enqueueSnackbar } = useSnackbar();
    const [isUploading, setIsUploading] = useState(false);

    const uploadFiles = useCallback(
        async (files: File[]): Promise<ClientDocument[]> => {
            if (files.length === 0) return [];
            setIsUploading(true);
            const uploaded: ClientDocument[] = [];
            try {
                for (const file of files) {
                    if (file.size > MAX_FILE_SIZE) {
                        enqueueSnackbar(
                            `${file.name} exceeds the ${MAX_FILE_SIZE / (1024 * 1024)}MB limit.`,
                            { variant: "error" },
                        );
                        continue;
                    }
                    try {
                        const doc = await uploadDocument({
                            filename: file.name,
                            file,
                            aiOptOut: true,
                            context,
                            notifyContext,
                        });
                        uploaded.push(doc);
                    } catch (err) {
                        enqueueSnackbar(
                            err instanceof Error && err.message
                                ? err.message
                                : `Couldn't upload ${file.name}.`,
                            { variant: "error" },
                        );
                    }
                }
            } finally {
                setIsUploading(false);
            }
            return uploaded;
        },
        [uploadDocument, enqueueSnackbar, context, notifyContext],
    );

    return { uploadFiles, isUploading };
};
